import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Filter, Eye, EyeOff, VolumeX, Volume2, MailWarning, UserPlus, Compass, LogIn, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { GateContext } from "@/context/GateContext";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import OfflineIndicator from "@/components/OfflineIndicator";
import PullToRefresh from "@/components/PullToRefresh";
import Timeline from "@/components/Timeline";
import EvidenceCalendar from "@/components/Calendar";
import AICoach from "@/components/AICoach";
import AskJustice from "@/components/AskJustice";
import OnboardingDisclaimer from "@/components/OnboardingDisclaimer";
import BottomNav from "@/components/BottomNav";
import Home from "@/pages/Home";
import Reports from "@/pages/Reports";
import More from "@/pages/More";
import VaultLocker from "@/components/VaultLocker";
import FinancialLedger from "@/components/FinancialLedger";
import CourtOrder from "@/components/CourtOrder";
import ParentChildComms from "@/components/ParentChildComms";
import PlanSelection from "@/pages/PlanSelection";
import { PinSetupDialog } from "@/components/PinPad";
import MatterOnboarding, { MatterBar, NewMatterDialog, StateRequirementsHub } from "@/components/Matter";
import DisclaimerFooter from "@/components/DisclaimerFooter";

function trialDaysLeft(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));
}

export default function Dashboard() {
  const { user, guest, logout, refresh, exitGuest } = useAuth();
  const [view, setView] = useState("home");
  const [evidence, setEvidence] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [activity, setActivity] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [courtOrder, setCourtOrder] = useState(null);
  const [highlight, setHighlight] = useState(null); // { tab, id }
  const [onboardAcked, setOnboardAcked] = useState(() => {
    try { return !!user?.onboarding_ack || localStorage.getItem("ek_onboarded") === "1"; } catch { return !!user?.onboarding_ack; }
  });
  const scrollMem = React.useRef({});
  const navTo = useCallback((v) => {
    scrollMem.current[view] = window.scrollY;
    setHighlight(null);
    setView(v);
  }, [view]);
  const jumpTo = useCallback((tab, id) => {
    scrollMem.current[view] = window.scrollY;
    setView(tab);
    setHighlight({ tab, id });
  }, [view]);
  React.useEffect(() => {
    if (highlight && highlight.tab === view) return; // a jump is pending; let target scroll itself
    const target = scrollMem.current[view] || 0;
    if (target <= 0) { window.scrollTo({ top: 0 }); return; }
    // Content on some tabs (e.g. Vault) mounts asynchronously, so a single scroll can be
    // clamped before the page grows tall enough. Retry until we reach the target.
    let cancelled = false;
    let tries = 0;
    const attempt = () => {
      if (cancelled) return;
      window.scrollTo({ top: target });
      tries += 1;
      if (Math.abs(window.scrollY - target) > 4 && tries < 18) {
        setTimeout(attempt, 90);
      }
    };
    requestAnimationFrame(attempt);
    return () => { cancelled = true; };
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const [audit, setAudit] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [coachOpen, setCoachOpen] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [quiet, setQuiet] = useState(false);
  const [pinSetupOpen, setPinSetupOpen] = useState(false);
  const [newSignal, setNewSignal] = useState(0);
  const [lockerUnlocked, setLockerUnlocked] = useState(false);
  const [skipPlan, setSkipPlan] = useState(() => sessionStorage.getItem("ek_skip_plan") === "1");
  const [matters, setMatters] = useState([]);
  const [activeMatterId, setActiveMatterId] = useState(null);
  const [matterSkip, setMatterSkip] = useState(() => sessionStorage.getItem("ek_skip_matter") === "1");
  const [newMatterOpen, setNewMatterOpen] = useState(false);
  const promptedRef = useRef(false);
  const { onSynced } = useOffline();
  const isGuest = guest && !user;
  const [signupPrompt, setSignupPrompt] = useState(false);
  const [calRefresh, setCalRefresh] = useState(0);
  const gate = useCallback((fn) => { if (isGuest) { setSignupPrompt(true); return false; } return fn ? fn() : true; }, [isGuest]);

  const loadEvidence = useCallback(async () => { try { const { data } = await api.get("/evidence", { params: activeMatterId ? { matter_id: activeMatterId } : {} }); setEvidence(data); } catch (e) { console.error("loadEvidence failed", e); } }, [activeMatterId]);
  const loadIncidents = useCallback(async () => { try { const { data } = await api.get("/incidents", { params: activeMatterId ? { matter_id: activeMatterId } : {} }); setIncidents(data); } catch (e) { console.error("loadIncidents failed", e); } }, [activeMatterId]);
  const loadActivity = useCallback(async () => { try { const { data } = await api.get("/activity", { params: activeMatterId ? { matter_id: activeMatterId } : {} }); setActivity(data); } catch (e) { console.error("loadActivity failed", e); } }, [activeMatterId]);
  const loadIntegrity = useCallback(async () => { if (!activeMatterId) { setIntegrity(null); return; } try { const { data } = await api.get(`/matters/${activeMatterId}/integrity`); setIntegrity(data); } catch { setIntegrity(null); } }, [activeMatterId]);
  const loadCourtOrder = useCallback(async () => { if (!activeMatterId) { setCourtOrder(null); return; } try { const { data } = await api.get("/orders"); setCourtOrder(data); } catch { setCourtOrder(null); } }, [activeMatterId]);
  const loadAudit = useCallback(async () => { try { const { data } = await api.get("/audit"); setAudit(data); } catch (e) { console.error("loadAudit failed", e); } }, []);
  const loadSubscription = useCallback(async () => { try { const { data } = await api.get("/subscription"); setSubscription(data); } catch (e) { console.error("loadSubscription failed", e); } }, []);
  const loadMatters = useCallback(async () => { try { const { data } = await api.get("/matters"); setMatters(data.matters); setActiveMatterId(data.active_matter_id); } catch (e) { console.error("loadMatters failed", e); } }, []);

  const reloadIncidents = useCallback(() => { loadIncidents(); loadActivity(); loadIntegrity(); }, [loadIncidents, loadActivity, loadIntegrity]);
  const reloadEvidence = useCallback(() => { loadEvidence(); loadActivity(); loadIntegrity(); }, [loadEvidence, loadActivity, loadIntegrity]);

  useEffect(() => { loadEvidence(); loadIncidents(); loadActivity(); loadAudit(); loadSubscription(); loadMatters(); }, [loadEvidence, loadIncidents, loadActivity, loadAudit, loadSubscription, loadMatters]);
  useEffect(() => { loadIntegrity(); loadCourtOrder(); }, [loadIntegrity, loadCourtOrder]);  useEffect(() => onSynced(() => { loadIncidents(); loadActivity(); loadIntegrity(); }), [onSynced, loadIncidents, loadActivity, loadIntegrity]);

  // First-run: nudge to set a Vault PIN once per session
  useEffect(() => {
    if (user && !user.has_vault_pin && !promptedRef.current && !sessionStorage.getItem("ek_pin_prompted")) {
      promptedRef.current = true;
      sessionStorage.setItem("ek_pin_prompted", "1");
      setPinSetupOpen(true);
    }
  }, [user]);

  // Quiet Mode: pause all audio while active
  useEffect(() => {
    if (quiet) document.querySelectorAll("audio").forEach((a) => a.pause());
  }, [quiet]);

  // Background archive imports: notify when one finishes even after leaving the import screen.
  const importSeenRef = useRef(null);
  useEffect(() => {
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get("/evidence/import-jobs");
        const jobs = data.jobs || [];
        const prev = importSeenRef.current;
        const cur = new Map(jobs.map((j) => [j.job_id, j.status]));
        if (prev !== null) {
          jobs.forEach((j) => {
            const was = prev.get(j.job_id);
            const isDone = j.status === "completed" || j.status === "failed";
            const wasDone = was === "completed" || was === "failed";
            if (isDone && !wasDone) {
              const name = j.archive || "Archive";
              if (j.status === "completed") {
                const extra = [
                  j.need_review ? `${j.need_review} need date review` : "",
                  j.unsupported ? `${j.unsupported} skipped` : "",
                ].filter(Boolean).join(" · ");
                toast.success(`Import finished: ${name}`, { description: `${j.added} file${j.added === 1 ? "" : "s"} extracted${extra ? " · " + extra : ""}` });
              } else {
                toast.error(`Import failed: ${name}`, { description: "Your evidence is safe — try importing again." });
              }
            }
          });
        }
        importSeenRef.current = cur;
      } catch { /* ignore */ }
      if (active) setTimeout(poll, 5000);
    };
    poll();
    return () => { active = false; };
  }, []);

  const resendVerification = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent"); }
    catch { toast.error("Could not send email"); }
  };

  const onNewEntry = () => { if (!gate()) return; setView("timeline"); setNewSignal((s) => s + 1); };
  const saveCoachEntry = async (text) => {
    try {
      await api.post("/incidents", { pure_fact: text, entry_type: "FACTS", matter_id: activeMatterId });
      toast.success("Added to your timeline");
      reloadIncidents(); setCoachOpen(false); setView("timeline");
    } catch { toast.error("Could not save entry"); }
  };
  const afterPin = async () => { await refresh(); loadAudit(); };

  if (subscription && subscription.needs_plan && !skipPlan) {
    return <PlanSelection onSkip={() => { sessionStorage.setItem("ek_skip_plan", "1"); setSkipPlan(true); }} />;
  }

  if (!isGuest && matters.length === 0 && !matterSkip) {
    return <MatterOnboarding onDone={() => loadMatters()} onSkip={() => { sessionStorage.setItem("ek_skip_matter", "1"); setMatterSkip(true); }} />;
  }

  const activeMatter = matters.find((m) => m.id === activeMatterId);
  const isFamily = activeMatter ? activeMatter.is_family : true;
  const switchMatter = async (id) => { try { await api.post(`/matters/${id}/activate`); setActiveMatterId(id); } catch (e) { console.error("switchMatter failed", e); toast.error("Could not switch case"); } };

  const trialDays = subscription?.status === "trialing" ? trialDaysLeft(subscription.trial_end) : 0;
  const isPro = subscription?.plan === "pro" && ["trialing", "active"].includes(subscription?.status);
  const proAccess = !!subscription?.litigator_pro;

  return (
    <GateContext.Provider value={{ isGuest, gate }}>
    <div className={`min-h-screen pb-24 overflow-x-hidden ${privacy ? "privacy-on" : ""}`} data-testid="dashboard">
      <header className="sticky top-0 z-30 border-b" style={{ background: "rgba(7,11,20,0.9)", backdropFilter: "blur(12px)", borderColor: "var(--ek-border)" }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <img src="/ek-logo.png" alt="EvidenceKeeper" className="h-9 w-9 object-contain" />
            <span className="font-head font-bold tracking-tight hidden sm:inline">EvidenceKeeper</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
            <MatterBar matters={matters} activeId={activeMatterId} onSwitch={switchMatter} onNew={() => gate(() => setNewMatterOpen(true))} />
            <OfflineIndicator />
            <StateRequirementsHub matter={activeMatter} />
            <Button onClick={() => setQuiet(true)} variant="outline" data-testid="quiet-mode-btn" title="Quiet Mode" className="rounded-md border w-9 h-9 p-0 shrink-0" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
              <VolumeX className="h-4 w-4" />
            </Button>
            <Button onClick={() => setPrivacy((p) => !p)} variant="outline" data-testid="privacy-toggle-btn" title="Privacy Mode" className="rounded-md border w-9 h-9 p-0 shrink-0" style={{ borderColor: "var(--ek-border)", background: privacy ? "var(--ek-surface-hover)" : "transparent" }}>
              {privacy ? <EyeOff className="h-4 w-4" style={{ color: "var(--ek-primary)" }} /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button onClick={() => gate(() => setCoachOpen(true))} variant="outline" data-testid="open-coach-btn" title="Justice" className="rounded-md border w-9 h-9 p-0 sm:w-auto sm:h-10 sm:px-4 shrink-0" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
              <Filter className="h-4 w-4 sm:mr-2" style={{ color: "var(--ek-primary)" }} /> <span className="hidden sm:inline">Justice</span>
            </Button>
          </div>
        </div>
      </header>

      {isGuest && (
        <div className="border-b" style={{ background: "rgba(46,130,246,0.08)", borderColor: "rgba(46,130,246,0.25)" }} data-testid="guest-banner">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center gap-3 text-sm flex-wrap">
            <Compass className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />
            <span style={{ color: "var(--ek-text-secondary)" }}>You're exploring as a guest — records won't be saved.</span>
            <button onClick={() => exitGuest()} data-testid="guest-signup-link" className="underline" style={{ color: "var(--ek-primary)" }}>Sign up to save</button>
          </div>
        </div>
      )}

      {user && !user.email_verified && (
        <div className="border-b" style={{ background: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.25)" }} data-testid="verify-banner">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center gap-3 text-sm flex-wrap">
            <MailWarning className="h-4 w-4" style={{ color: "var(--ek-warning)" }} />
            <span style={{ color: "var(--ek-text-secondary)" }}>Please verify your email to fully activate your account.</span>
            <button onClick={resendVerification} data-testid="resend-verification-btn" className="underline" style={{ color: "var(--ek-warning)" }}>Resend email</button>
          </div>
        </div>
      )}

      {subscription?.status === "trialing" && (
        <div className="border-b" style={{ background: "rgba(46,130,246,0.1)", borderColor: "rgba(46,130,246,0.3)" }} data-testid="trial-banner">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center gap-3 text-sm flex-wrap">
            <span className="ek-stamp" style={{ color: "var(--ek-primary)", borderColor: "rgba(46,130,246,0.4)" }}>TRIAL STATUS</span>
            <span style={{ color: "var(--ek-text)" }}><b>{trialDays} day{trialDays === 1 ? "" : "s"} remaining</b></span>
            <span style={{ color: "var(--ek-text-muted)" }}>· Cancel anytime before then and your card will not be charged.</span>
            <button onClick={() => setView("more")} data-testid="banner-manage-btn" className="underline" style={{ color: "var(--ek-primary)" }}>Manage</button>
          </div>
        </div>
      )}

      {!onboardAcked && <OnboardingDisclaimer onAcknowledge={() => setOnboardAcked(true)} />}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">        {view === "home" && (
          <Home user={user} activity={activity} counts={{ evidence: evidence.length, incidents: incidents.length }} integrity={integrity} courtOrder={courtOrder} isFamily={isFamily} matterId={activeMatterId} matterTitle={activeMatter?.title} onNav={navTo} onJump={jumpTo} onNewEntry={onNewEntry} />
        )}
        {view === "timeline" && (
          <PullToRefresh onRefresh={async () => { await reloadIncidents(); }}>
            <Timeline incidents={incidents} evidence={evidence} reload={reloadIncidents} startNew={newSignal} isPro={isPro} isFamily={isFamily} matterId={activeMatterId} highlight={highlight?.tab === "timeline" ? highlight.id : null} onHighlightDone={() => setHighlight(null)} />
          </PullToRefresh>
        )}
        {view === "calendar" && (
          <PullToRefresh onRefresh={async () => { setCalRefresh((x) => x + 1); await loadIntegrity(); }}>
            <EvidenceCalendar evidence={evidence} incidents={incidents} matterId={activeMatterId} reloadIntegrity={loadIntegrity} refreshSignal={calRefresh} />
          </PullToRefresh>
        )}
        {view === "vault" && (
          <VaultLocker user={user} evidence={evidence} reload={reloadEvidence} unlocked={lockerUnlocked} setUnlocked={setLockerUnlocked} onSetupPin={() => setPinSetupOpen(true)} matterId={activeMatterId} matters={matters} highlight={highlight?.tab === "vault" ? highlight.id : null} onHighlightDone={() => setHighlight(null)} />
        )}
        {view === "reports" && (
          <Reports user={user} incidents={incidents} matterId={activeMatterId} trialing={subscription?.status === "trialing"} onSetupPin={() => setPinSetupOpen(true)} reloadAudit={loadAudit} />
        )}
        {view === "damages" && (
          <FinancialLedger matterId={activeMatterId} proAccess={proAccess} onUpgrade={() => setView("more")} user={user} onSetupPin={() => setPinSetupOpen(true)} />
        )}
        {view === "orders" && (
          <CourtOrder matterId={activeMatterId} evidence={evidence} activeMatter={activeMatter} onNav={navTo} />
        )}
        {view === "comms" && (
          <ParentChildComms matterId={activeMatterId} onBack={() => navTo("home")} />
        )}
        {view === "ask" && (
          <AskJustice matterId={activeMatterId} evidenceCount={evidence.length} onOpenEvidence={(id) => { setHighlight({ tab: "vault", id }); navTo("vault"); }} />
        )}
        {view === "more" && (
          <More user={user} audit={audit} subscription={subscription} reloadSubscription={loadSubscription} onSetupPin={() => setPinSetupOpen(true)} privacy={privacy} setPrivacy={setPrivacy} onQuiet={() => setQuiet(true)} onResendVerification={resendVerification} logout={logout} onNav={navTo} proAccess={proAccess} />
        )}
        <DisclaimerFooter />
      </main>

      <BottomNav view={view} setView={navTo} />
      {view !== "ask" && (
        <button
          onClick={() => navTo("ask")}
          data-testid="ask-justice-fab"
          className="fixed right-4 z-40 flex items-center gap-2 rounded-full px-4 py-3 shadow-lg transition-transform hover:scale-105"
          style={{ bottom: "calc(env(safe-area-inset-bottom) + 72px)", background: "var(--ek-primary)", color: "#fff" }}
          aria-label="Ask Justice about your evidence"
        >
          <Sparkles className="h-5 w-5" />
          <span className="text-sm font-medium hidden sm:inline">Ask Justice</span>
        </button>
      )}
      <NewMatterDialog open={newMatterOpen} onOpenChange={setNewMatterOpen} onDone={() => loadMatters()} />
      <AICoach open={coachOpen} onOpenChange={setCoachOpen} onSaveEntry={saveCoachEntry} />
      <PinSetupDialog open={pinSetupOpen} onOpenChange={setPinSetupOpen} hasPin={!!user?.has_vault_pin} onDone={afterPin} />

      <AnimatePresence>
        {quiet && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-6"
            style={{ background: "rgba(3,5,10,0.94)" }}
            data-testid="quiet-overlay"
          >
            <VolumeX className="h-12 w-12" style={{ color: "var(--ek-steel)" }} strokeWidth={1.2} />
            <div className="text-center">
              <div className="font-head font-semibold text-xl tracking-tight">Quiet Mode</div>
              <div className="ek-mono-label mt-2">AUDIO MUTED · SCREEN DIMMED</div>
            </div>
            <Button onClick={() => setQuiet(false)} data-testid="quiet-resume-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              <Volume2 className="h-4 w-4 mr-2" /> Resume
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <SignupPrompt open={signupPrompt} onClose={() => setSignupPrompt(false)} onSignup={() => { setSignupPrompt(false); exitGuest(); }} />
    </div>
    </GateContext.Provider>
  );
}

function SignupPrompt({ open, onClose, onSignup }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="signup-prompt">
        <DialogHeader>
          <DialogTitle className="font-head font-semibold tracking-tight flex items-center gap-2"><UserPlus className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Create a free account</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            Please sign up or log in to save your records and unlock full AI power.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button data-testid="signup-prompt-cta" onClick={onSignup} className="w-full rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            <LogIn className="mr-2 h-4 w-4" /> Sign up or log in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
